import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AdminClub,
  ClubProfile,
  ClubWithFacilities,
  CreateClubRequest,
  MyClub,
  UpdateClubRequest,
} from '../model/types';
import {
  createClub,
  deleteClub,
  getAdminClubs,
  getClub,
  getClubsWithFacilities,
  getMyClubs,
  updateClub,
} from './clubApi';

/** Klucze pamieci podrecznej modulu klubow. */
export const clubKeys = {
  all: ['clubs'] as const,
  mine: () => [...clubKeys.all, 'mine'] as const,
  detail: (clubId: string) => [...clubKeys.all, 'detail', clubId] as const,
  withFacilities: () => [...clubKeys.all, 'facilities'] as const,
  adminList: () => [...clubKeys.all, 'admin-list'] as const,
};

/** Pobiera kluby uzytkownika — zmieniaja sie rzadko, wiec trzymamy je 5 minut. */
export function useMyClubsQuery(): UseQueryResult<MyClub[]> {
  return useQuery({
    queryKey: clubKeys.mine(),
    queryFn: getMyClubs,
    staleTime: 5 * 60 * 1000,
  });
}

/** Pobiera kluby z obiektami — zrodlo pickera kortow na ekranie infrastruktury. */
export function useClubsWithFacilitiesQuery(): UseQueryResult<ClubWithFacilities[]> {
  return useQuery({
    queryKey: clubKeys.withFacilities(),
    queryFn: getClubsWithFacilities,
    staleTime: 5 * 60 * 1000,
  });
}

/** Pobiera wizytowke klubu po identyfikatorze — dane zmieniaja sie rzadko. */
export function useClubQuery(clubId: string | undefined): UseQueryResult<ClubProfile> {
  return useQuery({
    queryKey: clubKeys.detail(clubId ?? ''),
    queryFn: () => getClub(clubId ?? ''),
    enabled: Boolean(clubId),
    staleTime: 5 * 60 * 1000,
  });
}

/** Pelna lista klubow — panel administratora. */
export function useAdminClubsQuery(enabled: boolean): UseQueryResult<AdminClub[]> {
  return useQuery({
    queryKey: clubKeys.adminList(),
    queryFn: getAdminClubs,
    enabled,
  });
}

/** Uniewaznia wszystkie widoki klubow po mutacji z panelu administratora. */
function useClubsInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: clubKeys.all });
  };
}

/** Tworzy klub z panelu ADMIN. */
export function useCreateClubMutation(): UseMutationResult<void, Error, CreateClubRequest> {
  const invalidate = useClubsInvalidation();
  return useMutation({ mutationFn: createClub, onSuccess: invalidate });
}

/** Edytuje dane podstawowe klubu (ADMIN). */
export function useUpdateClubMutation(): UseMutationResult<
  void,
  Error,
  { clubId: string; request: UpdateClubRequest }
> {
  const invalidate = useClubsInvalidation();
  return useMutation({
    mutationFn: ({ clubId, request }) => updateClub(clubId, request),
    onSuccess: invalidate,
  });
}

/** Trwale usuwa klub (ADMIN). */
export function useDeleteClubMutation(): UseMutationResult<void, Error, string> {
  const invalidate = useClubsInvalidation();
  return useMutation({ mutationFn: deleteClub, onSuccess: invalidate });
}
